import { Camera as CameraIcon, RefreshCw, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Modal } from "./Modal";

const MAX_SIDE = 1600; // keeps a photo around 300–600 KB: sharp enough for slips and the estimate page
const CAMERA_KEY = "wrp.checkinCamera";

/** A JPEG of the image, at most MAX_SIDE pixels on its long side, as base64 without the data: prefix. */
function toJpeg(source: CanvasImageSource, w: number, h: number): string {
  const scale = Math.min(1, MAX_SIDE / Math.max(w, h));
  const c = document.createElement("canvas");
  c.width = Math.round(w * scale);
  c.height = Math.round(h * scale);
  c.getContext("2d")!.drawImage(source, 0, 0, c.width, c.height);
  return c.toDataURL("image/jpeg", 0.85).split(",")[1]!;
}

/** A camera (or a photo file instead) in a window; `onPhoto` saves each photo. */
export function CameraDialog({ title, hint, onPhoto, onClose, busy, status }: {
  title: string; hint?: string; busy: boolean; status?: string | null;
  onPhoto: (jpegBase64: string) => Promise<void>; onClose: () => void;
}) {
  return (
    <Modal title={title} onClose={onClose}>
      <CameraView hint={hint} busy={busy} status={status} onPhoto={onPhoto} busyText={title.includes("AI") ? "Saving and identifying…" : "Saving…"}>
        <button type="button" className="btn outline" onClick={onClose}>Done</button>
      </CameraView>
    </Modal>
  );
}

/** Live webcam preview with Take photo / Upload / camera choice. Extra buttons go in `children`. */
export function CameraView({ hint, onPhoto, busy, status, busyText = "Saving…", children }: {
  hint?: string; busy: boolean; status?: string | null; busyText?: string;
  onPhoto: (jpegBase64: string) => Promise<void>; children?: React.ReactNode;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [deviceId, setDeviceId] = useState<string>(() => { try { return localStorage.getItem(CAMERA_KEY) ?? ""; } catch { return ""; } });
  const [error, setError] = useState("");
  const [frozen, setFrozen] = useState<string | null>(null);

  useEffect(() => {
    let stream: MediaStream | null = null;
    let cancelled = false;
    setError("");
    navigator.mediaDevices?.getUserMedia({ video: { ...(deviceId ? { deviceId: { exact: deviceId } } : {}), width: { ideal: 1920 }, height: { ideal: 1080 } } })
      .then(async (s) => {
        if (cancelled) { s.getTracks().forEach((t) => t.stop()); return; }
        stream = s;
        if (video.current) { video.current.srcObject = s; await video.current.play().catch(() => undefined); }
        setDevices((await navigator.mediaDevices.enumerateDevices()).filter((d) => d.kind === "videoinput"));
      })
      .catch((e: Error) => setError(e.name === "NotAllowedError" ? "The browser was not allowed to use the camera. Allow it in the address bar, or upload a photo instead." : `No camera: ${e.message}`));
    if (!navigator.mediaDevices) setError("This browser cannot use a camera here. Upload a photo instead.");
    return () => { cancelled = true; stream?.getTracks().forEach((t) => t.stop()); };
  }, [deviceId]);

  const save = async (b64: string) => {
    setFrozen(`data:image/jpeg;base64,${b64}`);
    try { await onPhoto(b64); } finally { setFrozen(null); }
  };
  const capture = () => {
    const v = video.current;
    if (!v || !v.videoWidth) return;
    void save(toJpeg(v, v.videoWidth, v.videoHeight));
  };
  const upload = async (f: File | undefined) => {
    if (!f) return;
    const bmp = await createImageBitmap(f);
    await save(toJpeg(bmp, bmp.width, bmp.height));
  };

  return (
      <div className="camera">
        {hint && <p className="hint">{hint}</p>}
        <div className="camera-view">
          {frozen ? <img src={frozen} alt="Captured photo" /> : <video ref={video} playsInline muted aria-label="Camera preview" />}
          {busy && <div className="camera-busy">{busyText}</div>}
        </div>
        {error && <p className="warn">{error}</p>}
        {status && <p className="camera-status" role="status">{status}</p>}
        <div className="btn-row wrap">
          <button type="button" className="btn primary" disabled={busy || !!error} onClick={capture}><CameraIcon size={16} /> Take photo</button>
          <label className="btn outline">
            <Upload size={16} /> Upload a photo
            <input type="file" accept="image/jpeg,image/png,image/webp" hidden disabled={busy}
              onChange={(e) => { void upload(e.target.files?.[0]); e.target.value = ""; }} />
          </label>
          {devices.length > 1 && (
            <label className="cam-pick"><RefreshCw size={14} />
              <select value={deviceId} aria-label="Camera" onChange={(e) => { setDeviceId(e.target.value); try { localStorage.setItem(CAMERA_KEY, e.target.value); } catch { /* ignore */ } }}>
                <option value="">Default camera</option>
                {devices.map((d, i) => <option key={d.deviceId} value={d.deviceId}>{d.label || `Camera ${i + 1}`}</option>)}
              </select>
            </label>
          )}
          {children}
        </div>
      </div>
  );
}
